/**
 * Cached state of the Skills ConfigCat flags (`skills`, `pptxSkills`, and the
 * Excel rollout flag, whose remote key is `excelskills`) for code that cannot
 * reach the ConfigCat client.
 *
 * The client lives in the PowerPoint workspace, which `excel/` and `shared/`
 * may not import (see `tools/import-boundary-check`), so the flag service
 * pushes resolved state in here and both hosts read it back out. This mirrors
 * `shared/featureFlags/excelExecutorModel.ts` and
 * `shared/featureFlags/excelTemplateBrand.ts`.
 *
 * Every unresolved state — no SDK key, disabled client, pending refresh,
 * ConfigCat unreachable — reads as disabled. A Skills gate never fails open.
 */

type PickerSkillsListener = () => void;

export interface PickerSkillsState {
  /** The flag client is live and its first resolution has completed. */
  readonly ready: boolean;
  readonly skills: boolean | null;
  readonly pptxSkills: boolean | null;
  readonly xlsxSkills: boolean | null;
  /**
   * Bumped on account/session reinitialization but not on a same-account flag
   * refresh, so a rollout change mid-run cannot invalidate an in-flight
   * submission while an account switch still does.
   */
  readonly contextGeneration: number;
}

const DISABLED_STATE: PickerSkillsState = Object.freeze({
  ready: false,
  skills: null,
  pptxSkills: null,
  xlsxSkills: null,
  contextGeneration: 0,
});

let state: PickerSkillsState = DISABLED_STATE;
const listeners = new Set<PickerSkillsListener>();

function isSameState(left: PickerSkillsState, right: PickerSkillsState): boolean {
  return (
    left.ready === right.ready &&
    left.skills === right.skills &&
    left.pptxSkills === right.pptxSkills &&
    left.xlsxSkills === right.xlsxSkills &&
    left.contextGeneration === right.contextGeneration
  );
}

/**
 * Called by the PowerPoint flag service on every meaningful cache transition.
 * Idempotent, so redundant publishes cost nothing and cannot loop listeners.
 */
export function publishPickerSkillsState(next: PickerSkillsState): void {
  if (isSameState(state, next)) return;
  state = Object.freeze({ ...next });
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.warn("[PickerSkills] Listener threw", error);
    }
  });
}

export function subscribePickerSkills(listener: PickerSkillsListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Atomic `skills && xlsxSkills` gate for the Excel add-in. */
export function isExcelSkillsEnabled(): boolean {
  return state.ready && state.skills === true && state.xlsxSkills === true;
}

/** Atomic `skills && pptxSkills` gate, for shared UI rendered in PowerPoint. */
export function isPowerPointPickerSkillsEnabled(): boolean {
  return state.ready && state.skills === true && state.pptxSkills === true;
}

export interface ExcelSkillsGateSnapshot {
  readonly enabled: boolean;
  /** Changes on account/session reinitialization, but not a same-account refresh. */
  readonly contextGeneration: number;
}

/** Atomically capture the complete Excel Skills decision for one submission. */
export function snapshotExcelSkillsGate(): ExcelSkillsGateSnapshot {
  return Object.freeze({
    enabled: isExcelSkillsEnabled(),
    contextGeneration: state.contextGeneration,
  });
}

export function isExcelSkillsGateSnapshotCurrent(snapshot: ExcelSkillsGateSnapshot): boolean {
  return snapshot.contextGeneration === state.contextGeneration;
}

/** Test seam. Production state only ever arrives through the flag service. */
export function resetPickerSkillsStateForTests(): void {
  state = DISABLED_STATE;
  listeners.clear();
}
